import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory, useLocation } from "react-router-dom";
import { useAppDispatch } from "../../../store";
import {
  fetchFeatureRequests,
  approveFeatureRequest,
  denyFeatureRequest,
  FeatureRequestItem,
  LicensePlan,
} from "../../../store/actions/PlanActions";
import Button from "../TailwindComponents/Button";
import { Dialog, Transition } from "@headlessui/react";
import {
  PiCheckCircleLight,
  PiXCircleLight,
  PiClockLight,
  PiXLight,
  PiEnvelopeSimpleLight,
  PiFunnelLight,
  PiUserLight,
  PiPuzzlePieceLight,
} from "react-icons/pi";

/* ─── Section label mapping (matches LicenseOverlay) ─── */
const SECTION_LABELS: Record<string, string> = {
  matters: "Case Management",
  clients: "Client Management",
  tasks: "Tasks & Deadlines",
  files: "Document Management",
  research: "AI Legal Research",
  tables: "AI Due Diligence Tables",
  analytics: "Analytics & Reporting",
  data_sources: "Knowledge Base (RAG)",
  templates: "Document Templates",
  client_pipeline: "Client Pipeline & AI Intake",
  finance: "Finance & Billing",
  people: "People Management",
  departments: "Departments",
  teams: "Teams",
  calendar: "Calendar",
  messages: "Message Center",
  review: "Contract Review",
  lawyers: "Lawyer Directory",
  finder: "Finder",
};

/* ─── Status badge ─── */
const StatusBadge: React.FC<{ status: string }> = ({ status }) => {
  const { t } = useTranslation();
  const styles: Record<string, string> = {
    pending: "bg-amber-100 dark:bg-amber-900/30 text-amber-700 dark:text-amber-400",
    approved: "bg-emerald-100 dark:bg-emerald-900/30 text-emerald-700 dark:text-emerald-400",
    denied: "bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-400",
  };
  const icons: Record<string, React.ReactNode> = {
    pending: <PiClockLight className="w-3 h-3" />,
    approved: <PiCheckCircleLight className="w-3 h-3" />,
    denied: <PiXCircleLight className="w-3 h-3" />,
  };
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-medium ${styles[status] || styles.pending}`}>
      {icons[status]}
      {t(status.charAt(0).toUpperCase() + status.slice(1))}
    </span>
  );
};

/* ─── Time ago helper ─── */
function timeAgo(dateStr: string): string {
  const diff = Date.now() - new Date(dateStr).getTime();
  const mins = Math.floor(diff / 60000);
  if (mins < 1) return "just now";
  if (mins < 60) return `${mins}m ago`;
  const hours = Math.floor(mins / 60);
  if (hours < 24) return `${hours}h ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${days}d ago`;
  return new Date(dateStr).toLocaleDateString();
}

/* ═══════════════════════════════════════════
   FeatureRequestsTab — admin-only component
   ═══════════════════════════════════════════ */

interface FeatureRequestsTabProps {
  addons: LicensePlan[];
}

const FeatureRequestsTab: React.FC<FeatureRequestsTabProps> = ({ addons }) => {
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const location = useLocation();
  const history = useHistory();

  const [requests, setRequests] = useState<FeatureRequestItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState<"all" | "pending" | "approved" | "denied">("all");
  const [selectedRequest, setSelectedRequest] = useState<FeatureRequestItem | null>(null);
  const [modalOpen, setModalOpen] = useState(false);

  /* ─── Load requests ─── */
  const loadRequests = useCallback(async () => {
    setLoading(true);
    const data = await dispatch(fetchFeatureRequests());
    if (data) setRequests(data);
    setLoading(false);
  }, [dispatch]);

  useEffect(() => {
    loadRequests();
  }, [loadRequests]);

  /* ─── Auto-open request from URL param (notification deep link) ─── */
  useEffect(() => {
    if (requests.length === 0) return;
    const params = new URLSearchParams(location.search);
    const requestId = params.get("request_id");
    if (requestId) {
      const match = requests.find((r) => r.id === parseInt(requestId, 10));
      if (match) {
        setSelectedRequest(match);
        setModalOpen(true);
        // Clean up the URL param
        params.delete("request_id");
        const newSearch = params.toString();
        history.replace(`${location.pathname}${newSearch ? `?${newSearch}` : ""}`);
      }
    }
  }, [requests, location.search, history, location.pathname]);

  /* ─── Filtered requests ─── */
  const filtered = useMemo(() => {
    if (filter === "all") return requests;
    return requests.filter((r) => r.status === filter);
  }, [requests, filter]);

  const pendingCount = useMemo(() => requests.filter((r) => r.status === "pending").length, [requests]);

  /* ─── Handlers ─── */
  const handleApprove = async (req: FeatureRequestItem, note: string) => {
    const success = await dispatch(approveFeatureRequest(req.id, note));
    if (success) {
      setModalOpen(false);
      setSelectedRequest(null);
      await loadRequests();
    }
  };

  const handleDeny = async (req: FeatureRequestItem, note: string) => {
    const success = await dispatch(denyFeatureRequest(req.id, note));
    if (success) {
      setModalOpen(false);
      setSelectedRequest(null);
      await loadRequests();
    }
  };

  const openDetail = (req: FeatureRequestItem) => {
    setSelectedRequest(req);
    setModalOpen(true);
  };

  /* ─── Render ─── */
  if (loading) {
    return (
      <div className="space-y-3 px-4">
        {Array.from({ length: 3 }).map((_, i) => (
          <div key={i} className="h-16 bg-gray-200 dark:bg-gray-700 rounded animate-fadePulse" />
        ))}
      </div>
    );
  }

  return (
    <div className="space-y-4 px-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div>
          <h4 className="text-sm font-medium text-gray-900 dark:text-gray-100">
            {t("Feature Requests")}
            {pendingCount > 0 && (
              <span className="ml-2 inline-flex items-center justify-center w-5 h-5 rounded-full bg-amber-100 dark:bg-amber-900/40 text-amber-700 dark:text-amber-400 text-xs font-semibold">
                {pendingCount}
              </span>
            )}
          </h4>
          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
            {t("Requests from team members for add-on or feature access")}
          </p>
        </div>
        {/* Filter pills */}
        <div className="inline-flex items-center rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 p-0.5">
          {(["all", "pending", "approved", "denied"] as const).map((f) => (
            <button
              key={f}
              onClick={() => setFilter(f)}
              className={`px-2.5 py-1 rounded-md text-xs font-medium transition-colors ${
                filter === f
                  ? "bg-gray-100 dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                  : "text-gray-500 dark:text-gray-400 hover:text-gray-700"
              }`}
            >
              {t(f.charAt(0).toUpperCase() + f.slice(1))}
            </button>
          ))}
        </div>
      </div>

      {/* Empty state */}
      {filtered.length === 0 ? (
        <div className="border rounded-lg bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 p-8 text-center">
          <PiEnvelopeSimpleLight className="w-8 h-8 text-gray-300 dark:text-gray-600 mx-auto mb-2" />
          <p className="text-sm text-gray-500 dark:text-gray-400">
            {filter === "all"
              ? t("No feature requests yet")
              : t("No {{status}} requests", { status: filter })}
          </p>
        </div>
      ) : (
        /* Request list */
        <div className="border rounded-lg overflow-hidden bg-white dark:bg-gray-900 border-gray-200 dark:border-gray-700 divide-y divide-gray-100 dark:divide-gray-800">
          {filtered.map((req) => {
            const sectionLabel = SECTION_LABELS[req.section] || req.section_display;
            const matchingAddon = addons.find((a) =>
              a.feature_gates?.sections?.includes(req.section)
            );
            return (
              <button
                key={req.id}
                onClick={() => openDetail(req)}
                className="w-full flex items-center gap-3 px-4 py-3 text-left hover:bg-gray-50 dark:hover:bg-gray-800/50 transition-colors"
              >
                {/* Icon */}
                <div className="flex items-center justify-center w-8 h-8 rounded-lg bg-gray-100 dark:bg-gray-800 flex-shrink-0">
                  <PiPuzzlePieceLight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                </div>
                {/* Info */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {sectionLabel}
                    </p>
                    <StatusBadge status={req.status} />
                  </div>
                  <div className="flex items-center gap-1.5 mt-0.5">
                    <PiUserLight className="w-3 h-3 text-gray-400" />
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {req.requested_by?.name || req.requested_by?.email || "Unknown"}
                    </p>
                    <span className="text-xs text-gray-400">·</span>
                    <p className="text-xs text-gray-400">{timeAgo(req.created_at)}</p>
                  </div>
                </div>
                {/* Price hint */}
                {matchingAddon && req.status === "pending" && (
                  <span className="text-xs text-gray-400 dark:text-gray-500 flex-shrink-0">
                    €{matchingAddon.price_monthly}/{t("mo")}
                  </span>
                )}
              </button>
            );
          })}
        </div>
      )}

      {/* ═══ Request Detail Modal ═══ */}
      <RequestDetailModal
        open={modalOpen}
        onClose={() => { setModalOpen(false); setSelectedRequest(null); }}
        request={selectedRequest}
        addons={addons}
        onApprove={handleApprove}
        onDeny={handleDeny}
      />
    </div>
  );
};

/* ═══════════════════════════════════════════
   RequestDetailModal — admin acts on request
   ═══════════════════════════════════════════ */

interface RequestDetailModalProps {
  open: boolean;
  onClose: () => void;
  request: FeatureRequestItem | null;
  addons: LicensePlan[];
  onApprove: (req: FeatureRequestItem, note: string) => Promise<void>;
  onDeny: (req: FeatureRequestItem, note: string) => Promise<void>;
}

const RequestDetailModal: React.FC<RequestDetailModalProps> = ({
  open,
  onClose,
  request,
  addons,
  onApprove,
  onDeny,
}) => {
  const { t } = useTranslation();
  const [note, setNote] = useState("");
  const [actionLoading, setActionLoading] = useState<"approve" | "deny" | null>(null);

  // Reset note when modal opens
  useEffect(() => {
    if (open) setNote("");
  }, [open]);

  if (!request) return null;

  const sectionLabel = SECTION_LABELS[request.section] || request.section_display;
  const matchingAddon = addons.find((a) => a.feature_gates?.sections?.includes(request.section));
  const isPending = request.status === "pending";

  const handleAction = async (action: "approve" | "deny") => {
    setActionLoading(action);
    try {
      if (action === "approve") {
        await onApprove(request, note);
      } else {
        await onDeny(request, note);
      }
    } finally {
      setActionLoading(null);
    }
  };

  return (
    <Transition show={open} as={React.Fragment}>
      <Dialog onClose={onClose} className="relative z-50">
        <Transition.Child
          as={React.Fragment}
          enter="ease-out duration-200"
          enterFrom="opacity-0"
          enterTo="opacity-100"
          leave="ease-in duration-150"
          leaveFrom="opacity-100"
          leaveTo="opacity-0"
        >
          <div className="fixed inset-0 bg-black/40" />
        </Transition.Child>

        <div className="fixed inset-0 flex items-center justify-center p-4">
          <Transition.Child
            as={React.Fragment}
            enter="ease-out duration-200"
            enterFrom="opacity-0 scale-95"
            enterTo="opacity-100 scale-100"
            leave="ease-in duration-150"
            leaveFrom="opacity-100 scale-100"
            leaveTo="opacity-0 scale-95"
          >
            <Dialog.Panel className="w-full max-w-md rounded-lg bg-white dark:bg-gray-900 border border-gray-200 dark:border-gray-700 shadow-lg">
              {/* Header */}
              <div className="flex items-start justify-between p-4 border-b border-gray-200 dark:border-gray-700">
                <div className="flex items-center gap-3">
                  <div className="flex items-center justify-center w-10 h-10 rounded-lg bg-gray-100 dark:bg-gray-800">
                    <PiPuzzlePieceLight className="w-5 h-5 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div>
                    <Dialog.Title className="font-medium text-gray-900 dark:text-gray-100 text-sm">
                      {sectionLabel}
                    </Dialog.Title>
                    <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                      {t("Feature Access Request")}
                    </p>
                  </div>
                </div>
                <button
                  onClick={onClose}
                  className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800"
                >
                  <PiXLight className="h-4 w-4 text-gray-600 dark:text-gray-400" />
                </button>
              </div>

              {/* Content */}
              <div className="p-4 space-y-4">
                {/* Requester info */}
                <div className="flex items-center gap-3 rounded-lg border border-gray-200 dark:border-gray-700 bg-gray-50 dark:bg-gray-800/50 p-3">
                  <div className="flex items-center justify-center w-8 h-8 rounded-full bg-white dark:bg-gray-700 shadow-sm">
                    <PiUserLight className="w-4 h-4 text-gray-600 dark:text-gray-400" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm font-medium text-gray-900 dark:text-gray-100 truncate">
                      {request.requested_by?.name || "Unknown"}
                    </p>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate">
                      {request.requested_by?.email}
                    </p>
                  </div>
                  <div className="text-right flex-shrink-0">
                    <StatusBadge status={request.status} />
                    <p className="text-xs text-gray-400 mt-1">{timeAgo(request.created_at)}</p>
                  </div>
                </div>

                {/* Message from requester */}
                {request.message && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t("Message")}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                      {request.message}
                    </p>
                  </div>
                )}

                {/* Addon price info */}
                {matchingAddon && (
                  <div className="flex items-center justify-between bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                    <div>
                      <p className="text-xs text-gray-500 dark:text-gray-400">{t("Related add-on")}</p>
                      <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                        {matchingAddon.display_name}
                      </p>
                    </div>
                    <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">
                      €{matchingAddon.price_monthly}/{t("mo")}
                    </p>
                  </div>
                )}

                {/* Admin note / resolution info */}
                {request.status !== "pending" && request.admin_note && (
                  <div>
                    <p className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1">{t("Admin note")}</p>
                    <p className="text-sm text-gray-700 dark:text-gray-300 bg-gray-50 dark:bg-gray-800/50 rounded-lg p-3 border border-gray-200 dark:border-gray-700">
                      {request.admin_note}
                    </p>
                  </div>
                )}

                {request.status !== "pending" && request.resolved_by && (
                  <p className="text-xs text-gray-400">
                    {request.status === "approved" ? t("Approved") : t("Denied")} {t("by")} {request.resolved_by.name}
                    {request.resolved_at && <> · {new Date(request.resolved_at).toLocaleDateString()}</>}
                  </p>
                )}

                {/* Admin action area (only for pending) */}
                {isPending && (
                  <>
                    <div>
                      <label className="text-xs font-medium text-gray-500 dark:text-gray-400 mb-1 block">
                        {t("Note (optional)")}
                      </label>
                      <textarea
                        value={note}
                        onChange={(e) => setNote(e.target.value)}
                        placeholder={t("Add a note for the requester...")}
                        className="w-full text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 px-3 py-2 text-gray-900 dark:text-gray-100 placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-primary-500 resize-none"
                        rows={2}
                      />
                    </div>

                    <div className="flex gap-2">
                      <Button
                        variant="primary"
                        size="small"
                        onClick={() => handleAction("approve")}
                        disabled={actionLoading !== null}
                        icon={<PiCheckCircleLight className="w-4 h-4" />}
                        className="flex-1"
                      >
                        {actionLoading === "approve" ? t("Approving...") : t("Approve")}
                      </Button>
                      <Button
                        variant="outline"
                        size="small"
                        onClick={() => handleAction("deny")}
                        disabled={actionLoading !== null}
                        className="flex-1 !text-red-600 dark:!text-red-400 hover:!bg-red-50 dark:hover:!bg-red-900/20"
                      >
                        {actionLoading === "deny" ? t("Denying...") : t("Deny")}
                      </Button>
                    </div>

                    <p className="text-xs text-gray-400 dark:text-gray-500 text-center">
                      {t("The requester will receive a notification about your decision.")}
                    </p>
                  </>
                )}
              </div>
            </Dialog.Panel>
          </Transition.Child>
        </div>
      </Dialog>
    </Transition>
  );
};

export default FeatureRequestsTab;
